import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { CommercialOffer, Offeree, OffereeStatus, OfferGroup, OfferItem, OfferItemType, Comment, CommercialOfferPreviewsResponse, ProcessOfferItemsRequest, ProcessOfferItemsResponse } from '../models/commercial-offer.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EndpointFactory } from './endpoint.service';
import { AuthService } from './auth.service';
import { map } from 'rxjs/operators';
import { ServiceResponse } from '../models/serviceResponse.model';

@Injectable({
  providedIn: 'root'
})
export class CommercialOffersService {
  constructor(
    private http: HttpClient,
    private endpoint: EndpointFactory,
    private authService: AuthService
  ) { }

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getAuthBearerToken();
    return new HttpHeaders().set('Authorization', 'Bearer ' + token);
  }

  /**
   * Get a new commercial offer for a given modelId
   * GET /api/commercialoffer/bom/:modelId
   */
  public getNewCommercialOffer(modelId: string): Observable<any> {
    return this.http.get<any>(
      this.endpoint.GetNewCommercialOffer.replace(':modelId', modelId),
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(response => {
        // Optionally map API response to model structure if needed
        return response;
      })
    );
  }

  // Get all offers for the user
  getOffersByUser(): Observable<CommercialOffer[]> {
    return this.http.get<any>(
      this.endpoint.GetOffersPreviews,
      { headers: this.getAuthHeaders() }
    ).pipe(
      map(response => {
        const offers = response.result?.commercialOfferPreviews || [];
        // Map the preview data to match the expected structure
        return offers.map((offerPreview: any) => ({
          id: offerPreview.id,
          name: offerPreview.name,
          status: offerPreview.status,
          lastUpdate: offerPreview.lastUpdate,
          bgModelId: offerPreview.bgModelId,
          // Initialize required properties with empty values
          logoUrl: '',
          clientId: '',
          view3dUrl: '',
          version: 1,
          to: '',
          re: '',
          // Initialize commercialOfferVersions with empty first version
          commercialOfferVersions: [{
            offerres: [],
            offerGroups: [],
            notes: '',
            comments: [],
            version: '1',
            lastUpdate: new Date(offerPreview.lastUpdate)
          }]
        }));
      })
    );
  }

  /**
   * Load commercial offer previews from backend
   * GET /api/commercialoffer/clients/preview
   */
  public loadPreviews(): Observable<ServiceResponse<CommercialOfferPreviewsResponse>> {
    return this.http.get<ServiceResponse<CommercialOfferPreviewsResponse>>(
      this.endpoint.GetOffersPreviews,
      { headers: this.getAuthHeaders() }
    );
  }

  // Get offer by modelId
  getOfferByModelId(modelId: string): Observable<CommercialOffer | null> {
    return this.getOffersByUser().pipe(
      map(offers => offers.find(o => o.bgModelId === modelId) || null)
    );
  }

  // Get offer by modelId and offerId
  getOfferByModelIdAndUnicname(modelId: string, offerId: string): Observable<CommercialOffer | null> {
    const url = this.endpoint.GetOfferById
      .replace(':modelId', modelId)
      .replace(':offerId', offerId);
    return this.http.get<any>(url, { headers: this.getAuthHeaders() }).pipe(
      map(response => {
        console.log('[getOfferByModelIdAndUnicname] Raw API response:', response);
        // Map API response to match the model structure
        if (response && response.result) {
          const offer = response.result;
          console.log('[getOfferByModelIdAndUnicname] Offer from API result:', offer);

          // Map the new API structure to our expected structure
          const mappedOffer: CommercialOffer = {
            id: offerId,
            name: offer.name,
            logoUrl: offer.logoUrl || '',
            clientId: offer.clientId,
            bgModelId: offer.bgModelId,
            view3dUrl: offer.view3dUrl || '',
            version: offer.version,
            lastUpdate: new Date(offer.lastUpdate),
            to: offer.to,
            re: offer.re,
            // Map versions array to commercialOfferVersions
            commercialOfferVersions: offer.versions?.map((version: any, index: number) => {
              // Preserve groupedSubOfferGroups structure as-is
              const mappedOfferGroups = version.offerGroups || [];

              console.log(`[getOfferByModelIdAndUnicname] Version ${index}:`, version);
              console.log(`[getOfferByModelIdAndUnicname] Version ${index} offerees:`, version.offerees);

              return {
                offerres: version.offerees?.map((offeree: any) => ({
                  ...offeree,
                  status: offeree.status?.toUpperCase(),
                  lastVisited: offeree.lastVisit || offeree.lastVisited
                })) || [],
                offerGroups: mappedOfferGroups,
                notes: version.notes || '',
                comments: version.comments || [],
                version: version.version,
                lastUpdate: new Date(version.lastUpdate)
              };
            }) || []
          };

          console.log('[getOfferByModelIdAndUnicname] Mapped offer:', mappedOffer);
          return mappedOffer;
        }
        return response;
      })
    );
  }

  // Get offer by offeree ID (for public access)
  getOffereeOffer(encodedOffereeId: string): Observable<CommercialOffer> {
    const url = this.endpoint.GetOffereeOffer.replace(':offereeId', encodedOffereeId);
    console.log('Calling GetOffereeOffer with URL:', url);
    return this.http.get<any>(url).pipe(
      map(response => {
        console.log('Raw API response:', response);
        // Map API response to match the model structure
        if (response && response.result) {
          const offer = response.result;
          console.log('Offer from API result:', offer);

          // Map 'offerees' from API to 'offerres' in model
          if (offer.offerees && !offer.offerres) {
            offer.offerres = offer.offerees;
            delete offer.offerees;
          }

          // Convert status to uppercase to match enum
          if (offer.offerres) {
            offer.offerres = offer.offerres.map((offeree: any) => ({
              ...offeree,
              status: offeree.status?.toUpperCase(),
              lastVisited: offeree.lastVisit || offeree.lastVisited
            }));
          }

          // Map logoUrl from clientLogoUrl if needed
          if (offer.clientLogoUrl && !offer.logoUrl) {
            offer.logoUrl = offer.clientLogoUrl;
          }

          // Map offer item types from string to enum
          if (offer.offerGroups) {
            offer.offerGroups = offer.offerGroups.map((group: any) => {
              // Map subgroups
              if (group.subOfferGroups) {
                group.subOfferGroups = group.subOfferGroups.map((subGroup: any) => {
                  if (subGroup.offerItems) {
                    subGroup.offerItems = subGroup.offerItems.map((item: any) => ({
                      ...item,
                      type: this.mapApiItemTypeToFrontend(item.type)
                    }));
                  }
                  return subGroup;
                });
              }

              // Map legacy offerItems
              if (group.offerItems) {
                group.offerItems = group.offerItems.map((item: any) => ({
                  ...item,
                  type: this.mapApiItemTypeToFrontend(item.type)
                }));
              }

              return group;
            });
          }

          console.log('Mapped offer:', offer);
          return offer;
        }
        return response;
      })
    );
  }

  // Update offer
  updateOffer(bomModelId: string, offerId: string, offer: CommercialOffer): Observable<CommercialOffer> {
    const url = this.endpoint.UpdateOffer
      .replace(':modelId', bomModelId)
      .replace(':offerId', offerId);
    return this.http.put<CommercialOffer>(url, offer, { headers: this.getAuthHeaders() });
  }

  // Edit specific version of an offer
  editVersion(bomModelId: string, offerId: string, version: string): Observable<CommercialOffer> {
    const url = this.endpoint.EditVersion
      .replace(':modeld', bomModelId)
      .replace(':offerId', offerId)
      .replace(':version', version);
    return this.http.get<CommercialOffer>(url, { headers: this.getAuthHeaders() });
  }

  // Create offer
  createOffer(bomModelId: string, offer: Omit<CommercialOffer, 'id'>): Observable<CommercialOffer> {
    const url = this.endpoint.CreateCommercialOffer.replace(':modelId', bomModelId);
    return this.http.post<CommercialOffer>(url, offer, { headers: this.getAuthHeaders() });
  }

  // Add comment
  addComment(modelId: string, offerId: string, version: string, comment: Omit<Comment, 'id' | 'date'>): Observable<Comment> {
    const url = this.endpoint.AddCommentToOffer
      .replace(':modelId', modelId)
      .replace(':offerId', offerId)
      .replace(':version', version);
    return this.http.post<Comment>(url, comment, { headers: this.getAuthHeaders() });
  }

  // Invite offeree
  inviteOfferee(bomModelId: string, offerId: string, version: string, email: string): Observable<Offeree> {
    return this.inviteOfferees(bomModelId, offerId, version, [{ email, accompanyingText: '' }]).pipe(
      map(offerees => offerees[0])
    );
  }

  // Invite multiple offerees with accompanying text
  inviteOfferees(bomModelId: string, offerId: string, version: string, invitePayload: Array<{email: string, accompanyingText: string}>): Observable<Offeree[]> {
    const url = this.endpoint.InviteOfferees
      .replace(':modelId', bomModelId)
      .replace(':offerId', offerId)
      .replace(':version', version);
    return this.http.post<Offeree[]>(url, invitePayload, { headers: this.getAuthHeaders() });
  }

  // Update offeree status
  updateOffereeStatus(modelId: string, offerId: string, offereeId: string, version: string, status: OffereeStatus): Observable<Offeree> {
    const url = this.endpoint.UpdateOffereeStatus
      .replace(':modelId', modelId)
      .replace(':offerId', offerId)
      .replace(':offereeId', offereeId)
      .replace(':version', version)
      .replace(':status', status);
    return this.http.patch<Offeree>(url, { status }, { headers: this.getAuthHeaders() });
  }

  // Delete offer
  deleteOffer(modelId: string, offerId: string): Observable<boolean> {
    const url = this.endpoint.DeleteOffer
      .replace(':modelId', modelId)
      .replace(':offerId', offerId);
    return this.http.delete<boolean>(url, { headers: this.getAuthHeaders() });
  }

  /**
   * Map API OfferItemType (0/1) to frontend OfferItemType (BOM/CUSTOMER)
   */
  private mapApiItemTypeToFrontend(apiType: any): OfferItemType {
    if (typeof apiType === 'string') {
      switch (apiType.toLowerCase()) {
        case 'bom':
          return OfferItemType.BOM;
        case 'customer':
          return OfferItemType.CUSTOMER;
        default:
          return OfferItemType.BOM; // Default to BOM
      }
    }

    switch (apiType) {
      case OfferItemType.BOM:
        return OfferItemType.BOM;
      case OfferItemType.CUSTOMER:
        return OfferItemType.CUSTOMER;
      default:
        return OfferItemType.BOM; // Default to BOM
    }
  }

  /**
   * Map frontend OfferItemType to API OfferItemType
   */
  public mapFrontendItemTypeToApi(frontendType: OfferItemType): OfferItemType {
    switch (frontendType) {
      case OfferItemType.BOM:
        return OfferItemType.BOM;
      case OfferItemType.CUSTOMER:
        return OfferItemType.CUSTOMER;
      default:
        return OfferItemType.BOM;
    }
  }

  /**
   * Get prices from document for auto-filling
   * POST /api/commercialoffer/ai-assistant/auto-complete
   */
  public getPricesFromDocument(offerItems: OfferItem[], file: File, additionalInstructions: string = ''): Observable<any> {
    const formData = new FormData();
    formData.append('OfferItems', JSON.stringify(offerItems));
    formData.append('Document', file);
    formData.append('AdditionalInstructions', additionalInstructions);

    // Log what we're sending
    console.log('=== getPricesFromDocument Request Details ===');
    console.log('URL:', this.endpoint.GetPricesFromDocument);
    console.log('Method: POST');
    console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
    console.log('OfferItems:', offerItems);
    console.log('AdditionalInstructions:', additionalInstructions || '(empty)');
    console.log('FormData keys: OfferItems, Document, AdditionalInstructions');
    console.log('==========================================');

    // No headers needed (no authorization) - using POST with formData
    return this.http.post<any>(
      this.endpoint.GetPricesFromDocument,
      formData
    );
  }

  /**
   * Extract items from document for auto-filling custom tables
   * POST /api/commercialoffer/ai-assistant/items/auto-complete
   */
  public extractItemsFromDocument(file: File, additionalInstructions: string = ''): Observable<ServiceResponse<OfferGroup[]>> {
    const formData = new FormData();
    formData.append('Document', file);
    formData.append('AdditionalInstructions', additionalInstructions);

    // Log what we're sending
    console.log('=== extractItemsFromDocument Request Details ===');
    console.log('URL:', this.endpoint.ExtractItemsFromDocument);
    console.log('Method: POST');
    console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
    console.log('AdditionalInstructions:', additionalInstructions || '(empty)');
    console.log('FormData keys: Document, AdditionalInstructions');
    console.log('==========================================');

    // No headers needed (no authorization) - using POST with formData
    return this.http.post<ServiceResponse<OfferGroup[]>>(
      this.endpoint.ExtractItemsFromDocument,
      formData
    );
  }

  /**
   * Process offer items using AI assistant
   * POST /api/commercialoffer/ai-assistant/items/process
   */
  public processOfferItems(request: ProcessOfferItemsRequest): Observable<ServiceResponse<ProcessOfferItemsResponse>> {
    // Log what we're sending
    console.log('=== processOfferItems Request Details ===');
    console.log('URL:', this.endpoint.ProcessOfferItems);
    console.log('Method: POST');
    console.log('User Message:', request.userMessage);
    console.log('Offer Groups:', request.offerGroups);
    console.log('==========================================');

    return this.http.post<ServiceResponse<ProcessOfferItemsResponse>>(
      this.endpoint.ProcessOfferItems,
      request,
      { headers: this.getAuthHeaders() }
    );
  }

  /**
   * Create an empty commercial offer for a given modelId
   * POST /commercialoffer/:modeld/create-empty
   */
  public createEmptyCommercialOffer(modelId: string, payload: { name: string; to: string; re: string }): Observable<any> {
    const url = this.endpoint.CreateEmptyCommercialOffer.replace(':modeld', modelId);
    return this.http.post<any>(url, payload, { headers: this.getAuthHeaders() });
  }

  /**
   * Get materials for a given modelId
   * GET /commercialoffer/bom/materials/:modelId
   */
  public getMaterials(modelId: string): Observable<any> {
    const url = this.endpoint.GetMaterials.replace(':modelId', modelId);
    return this.http.get<any>(url, { headers: this.getAuthHeaders() });
  }

  /**
   * Add a new version to an offer (patch)
   * PATCH /commercialoffer/:modeld/:offerId/v/add-version
   */
  public addNewVersion(modelId: string, offerId: string, offer: any): Observable<any> {
    const url = this.endpoint.AddNewVersion.replace(':modeld', modelId).replace(':offerId', offerId);
    return this.http.patch<any>(url, offer, { headers: this.getAuthHeaders() });
  }

  /**
   * PATCH /commercialoffer/bom/materials/:modelId
   * Add a new offer version for a model (for new offer creation via PATCH)
   */
  public patchNewOfferVersion(modelId: string, offer: any): Observable<any> {
    const url = this.endpoint.GetMaterials.replace('/materials/:modelId', '/materials/' + modelId);
    // The above line ensures the URL is /commercialoffer/bom/materials/:modelId
    return this.http.patch<any>(url, offer, { headers: this.getAuthHeaders() });
  }

  /**
   * PATCH /commercialoffer/:modeld/:offerId/v/:version/edit
   * Edit a specific version of an offer
   */
  public patchEditVersion(modelId: string, offerId: string, version: string, offer: any): Observable<any> {
    const url = this.endpoint.EditVersion
      .replace(':modeld', modelId)
      .replace(':offerId', offerId)
      .replace(':version', version);
    return this.http.patch<any>(url, offer, { headers: this.getAuthHeaders() });
  }
}
